import React, { useState, useEffect, useMemo, Fragment } from 'react';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  CheckIcon,
  ArrowDownTrayIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { Combobox, Transition } from '@headlessui/react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  BANKS,
  CURRENCIES,
  ACCOUNT_TYPES_OFX,
  ACCOUNT_TYPES_QBO,
  searchBanks,
  getCountries
} from '../../utils/bankData';

const ExportConfigModal = ({
  isOpen,
  onClose,
  format, // 'OFX' or 'QBO'
  transactionId,
  fileName
}) => {
  // Form state
  const [selectedBank, setSelectedBank] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [accountId, setAccountId] = useState('');
  const [accountType, setAccountType] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [fiId, setFiId] = useState('');
  const [isManualEntry, setIsManualEntry] = useState(false);

  // UI state
  const [isVisible, setIsVisible] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  // Initialize modal visibility
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      const timer = setTimeout(() => setIsVisible(false), 150);
      return () => clearTimeout(timer);
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isGenerating && onClose) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, isGenerating, onClose]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, format]);

  // Set default account type when format changes
  useEffect(() => {
    if (format === 'OFX' && !accountType) {
      setAccountType('CHECKING');
    } else if (format === 'QBO' && !accountType) {
      setAccountType('Bank');
    }
  }, [format]);

  // Auto-populate FI ID when bank is selected
  useEffect(() => {
    if (selectedBank && selectedBank.fiId && !isManualEntry) {
      setFiId(selectedBank.fiId);
      if (selectedBank.currency) {
        setCurrency(selectedBank.currency);
      }
    }
  }, [selectedBank, isManualEntry]);

  // Filter banks based on search query
  const filteredBanks = useMemo(() => {
    if (!searchQuery) return BANKS;
    return searchBanks(searchQuery);
  }, [searchQuery]);

  const resetForm = () => {
    setSelectedBank(null);
    setSearchQuery('');
    setAccountId('');
    setAccountType(format === 'OFX' ? 'CHECKING' : 'Bank');
    setCurrency('USD');
    setFiId('');
    setIsManualEntry(false);
    setError('');
    setValidationErrors({});
  };

  const handleBankSelect = (bank) => {
    if (bank && bank.value === 'other') {
      setIsManualEntry(true);
      setSelectedBank({ name: 'Other (Manual Entry)', fiId: '' });
      setFiId('');
    } else {
      setIsManualEntry(false);
      setSelectedBank(bank);
    }
    setValidationErrors(prev => ({ ...prev, bank: '' }));
  };

  const validateForm = () => {
    const errors = {};

    if (!selectedBank) {
      errors.bank = 'Please select a bank';
    }

    if (!accountId.trim()) {
      errors.accountId = 'Account ID is required';
    }

    if (!accountType) {
      errors.accountType = 'Account type is required';
    }

    if (!currency) {
      errors.currency = 'Currency is required';
    }

    if (!fiId.trim()) {
      errors.fiId = 'Financial Institution ID is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleGenerate = async () => {
    // Validate form
    if (!validateForm()) {
      setError('Please fill in all required fields');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      const functions = getFunctions();
      const generateExport = httpsCallable(functions, 'downloadFormat');

      console.log(`📤 Generating ${format} export for transaction ${transactionId}`);

      const config = {
        bankName: selectedBank.name,
        fiId: fiId.trim(),
        accountId: accountId.trim(),
        accountType: accountType,
        currency: currency,
        accountName: selectedBank.name // For QBO
      };

      const result = await generateExport({
        transactionId,
        format,
        config
      });

      console.log('✅ Export generated:', result.data);

      // Download the file
      const downloadUrl = result.data.downloadUrl;
      const downloadFileName = result.data.fileName;

      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log('✅ Download triggered');

      // Show success message briefly before closing
      setTimeout(() => {
        onClose();
      }, 500);

    } catch (error) {
      console.error('Failed to generate export:', error);

      let errorMessage = 'Failed to generate export. Please try again.';
      if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  const accountTypes = format === 'OFX' ? ACCOUNT_TYPES_OFX : ACCOUNT_TYPES_QBO;

  if (!isVisible) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
        isOpen
          ? 'opacity-100 backdrop-blur-sm bg-black/50'
          : 'opacity-0 pointer-events-none'
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="export-modal-title"
    >
      <div
        className={`relative w-full max-w-2xl bg-white rounded-xl shadow-2xl transform transition-all duration-300 ${
          isOpen ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-200">
          <div>
            <h2
              id="export-modal-title"
              className="text-xl font-semibold text-gray-900"
            >
              Export to {format === 'OFX' ? 'OFX 2.0' : 'QuickBooks (IIF)'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Configure export settings for {fileName}
            </p>
          </div>
          {onClose && !isGenerating && (
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6 space-y-5 max-h-[60vh] overflow-y-auto">
          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
              <ExclamationCircleIcon className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* Bank Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Bank <span className="text-red-500">*</span>
            </label>
            <Combobox value={selectedBank} onChange={handleBankSelect}>
              <div className="relative">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Combobox.Input
                    className={`w-full pl-10 pr-10 py-2.5 border ${
                      validationErrors.bank ? 'border-red-300' : 'border-gray-300'
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    placeholder="Search for your bank..."
                    displayValue={(bank) => bank?.name || ''}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Combobox.Options className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-lg py-1 text-base overflow-auto focus:outline-none border border-gray-200">
                    {filteredBanks.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-600">
                        No banks found. Try a different search or select "Other" for manual entry.
                      </div>
                    ) : (
                      filteredBanks.map((bank, index) => (
                        <Combobox.Option
                          key={`${bank.fiId}-${index}`}
                          value={bank}
                          className={({ active }) =>
                            `cursor-pointer select-none relative py-2.5 px-4 ${
                              active ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                            }`
                          }
                        >
                          {({ selected, active }) => (
                            <div className="flex items-center justify-between">
                              <div>
                                <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                  {bank.name}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {bank.country} • FI ID: {bank.fiId}
                                </span>
                              </div>
                              {selected && (
                                <CheckIcon className="h-5 w-5 text-blue-600" />
                              )}
                            </div>
                          )}
                        </Combobox.Option>
                      ))
                    )}

                    {/* Other (Manual Entry) Option */}
                    <Combobox.Option
                      value={{ name: 'Other (Manual Entry)', fiId: '', value: 'other' }}
                      className={({ active }) =>
                        `cursor-pointer select-none relative py-2.5 px-4 border-t border-gray-200 ${
                          active ? 'bg-amber-50 text-amber-900' : 'text-gray-900'
                        }`
                      }
                    >
                      {({ selected }) => (
                        <div className="flex items-center justify-between">
                          <div>
                            <span className={`block ${selected ? 'font-semibold' : 'font-normal'}`}>
                              Other (Manual Entry)
                            </span>
                            <span className="text-xs text-gray-500">
                              Enter bank details manually
                            </span>
                          </div>
                          {selected && (
                            <CheckIcon className="h-5 w-5 text-amber-600" />
                          )}
                        </div>
                      )}
                    </Combobox.Option>
                  </Combobox.Options>
                </Transition>
              </div>
            </Combobox>
            {validationErrors.bank && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.bank}</p>
            )}
          </div>

          {/* Financial Institution ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Financial Institution ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={fiId}
              onChange={(e) => {
                setFiId(e.target.value);
                setValidationErrors(prev => ({ ...prev, fiId: '' }));
              }}
              disabled={!isManualEntry && selectedBank?.fiId}
              placeholder="e.g., 5959, HBUK, CBA"
              className={`w-full px-4 py-2.5 border ${
                validationErrors.fiId ? 'border-red-300' : 'border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500`}
            />
            {validationErrors.fiId && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.fiId}</p>
            )}
            {!isManualEntry && selectedBank?.fiId && (
              <p className="mt-1 text-sm text-gray-500">Auto-populated from selected bank</p>
            )}
          </div>

          {/* Account ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Account Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={accountId}
              onChange={(e) => {
                setAccountId(e.target.value);
                setValidationErrors(prev => ({ ...prev, accountId: '' }));
              }}
              placeholder="Your account number"
              className={`w-full px-4 py-2.5 border ${
                validationErrors.accountId ? 'border-red-300' : 'border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
            />
            {validationErrors.accountId && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.accountId}</p>
            )}
          </div>

          {/* Account Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Account Type <span className="text-red-500">*</span>
            </label>
            <select
              value={accountType}
              onChange={(e) => {
                setAccountType(e.target.value);
                setValidationErrors(prev => ({ ...prev, accountType: '' }));
              }}
              className={`w-full px-4 py-2.5 border ${
                validationErrors.accountType ? 'border-red-300' : 'border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
            >
              <option value="">Select account type</option>
              {accountTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            {validationErrors.accountType && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.accountType}</p>
            )}
          </div>

          {/* Currency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Currency <span className="text-red-500">*</span>
            </label>
            <select
              value={currency}
              onChange={(e) => {
                setCurrency(e.target.value);
                setValidationErrors(prev => ({ ...prev, currency: '' }));
              }}
              className={`w-full px-4 py-2.5 border ${
                validationErrors.currency ? 'border-red-300' : 'border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
            >
              {CURRENCIES.map((curr) => (
                <option key={curr.code} value={curr.code}>
                  {curr.symbol} {curr.name} ({curr.code})
                </option>
              ))}
            </select>
            {validationErrors.currency && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.currency}</p>
            )}
          </div>

          {/* Info Box */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> This configuration is only used for this export. Your data is not stored or shared.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 pt-4 border-t border-gray-200 bg-gray-50">
          {!isGenerating && (
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className={`flex items-center space-x-2 px-6 py-2 text-sm font-medium text-white rounded-lg transition-colors shadow-sm ${
              format === 'OFX'
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-purple-600 hover:bg-purple-700'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isGenerating ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Generating...</span>
              </>
            ) : (
              <>
                <ArrowDownTrayIcon className="h-4 w-4" />
                <span>Generate & Download</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportConfigModal;
